import { useId } from "react";
import { formatMoney } from "../../shared/pdp/utils/pricing";
import { getAddonDescription, getAddonModalId } from "./copy";
import {
  getRecommendationLabel,
  getRecommendationReasonTag,
} from "./recommendations";
import {
  getAddonTotals,
  getDefaultQuantities,
  getProductQuantity,
  getVariantLabel,
  getVariantUnitPrice,
} from "./selection";
import type {
  AddonQuantities,
  PersonalizedAddonsSeason,
  SuggestedAddon,
} from "./types";

declare global {
  interface Window {
    openModal?: (modalId: string) => void;
  }
}

type AddonCardProps = {
  addon: SuggestedAddon;
  onAdd: (addon: SuggestedAddon) => void;
  onQuantityChange: (
    addon: SuggestedAddon,
    variantId: string,
    quantity: number,
  ) => void;
  onRemove: (addon: SuggestedAddon) => void;
  quantities?: AddonQuantities;
  lawnSize: number | null;
  season: PersonalizedAddonsSeason;
  showRecommendationReason?: boolean;
  showStartingPrice?: boolean;
};

export function AddonCard({
  addon,
  onAdd,
  onQuantityChange,
  onRemove,
  quantities = {},
  lawnSize,
  season,
  showRecommendationReason = false,
  showStartingPrice = false,
}: AddonCardProps) {
  const headingId = useId();
  const description = getAddonDescription(addon);
  const detailsModalId = getAddonModalId(addon);
  const hasDetailsModal = Boolean(
    detailsModalId && document.getElementById(detailsModalId),
  );
  const recommendationReasonTag = showRecommendationReason
    ? getRecommendationReasonTag(addon, season)
    : null;
  const defaultQuantities = getDefaultQuantities(addon);
  const isSelected = getProductQuantity(quantities) > 0;
  const isAvailable = Object.keys(defaultQuantities).length > 0;
  const recommendationLabel = getRecommendationLabel(addon, lawnSize);
  const displayedTotals = getAddonTotals(
    addon,
    isSelected ? quantities : defaultQuantities,
  );
  const hasDisplayedDiscount =
    displayedTotals.compareAtPrice > displayedTotals.price;

  return (
    <article
      aria-labelledby={headingId}
      className={`personalized-addons__card${
        isSelected ? " personalized-addons__card--selected" : ""
      }`}
      data-test={`personalized-addon-${addon.productId}`}
    >
      <div className="personalized-addons__card-header">
        <div className="personalized-addons__card-image-frame">
          {addon.imageUrl ? (
            <img
              alt=""
              className="personalized-addons__card-image"
              loading="lazy"
              src={addon.imageUrl}
            />
          ) : (
            <span
              aria-hidden="true"
              className="material-symbols-rounded personalized-addons__card-placeholder"
            >
              grass
            </span>
          )}
        </div>
        <div className="personalized-addons__card-copy">
          {recommendationReasonTag ? (
            <p className="personalized-addons__reason-tag">
              {recommendationReasonTag}
            </p>
          ) : null}
          <h2 className="personalized-addons__card-title" id={headingId}>
            {addon.title}
          </h2>
          <p className="personalized-addons__card-description">{description}</p>
          {hasDetailsModal ? (
            <button
              className="personalized-addons__card-details"
              data-test={`personalized-addon-details-${addon.productId}`}
              onClick={() => {
                if (detailsModalId && typeof window.openModal === "function") {
                  window.openModal(detailsModalId);
                }
              }}
              type="button"
            >
              Mehr erfahren
            </button>
          ) : null}
        </div>
      </div>

      {isSelected && recommendationLabel ? (
        <>
          <div className="personalized-addons__divider">
            <div></div>
          </div>
          <p
            className="personalized-addons__recommendation"
            data-test={`personalized-addon-recommendation-${addon.productId}`}
          >
            {recommendationLabel}
          </p>
        </>
      ) : null}

      {isSelected ? (
        <div
          className="personalized-addons__variant-list"
          data-test={`personalized-addon-variants-${addon.productId}`}
        >
          {addon.variants.map((variant) => {
            const quantity = quantities[variant.variantId] ?? 0;
            const unitPrice = getVariantUnitPrice(variant);
            const compareAtPrice = variant.compareAtPriceInCents;
            const hasCompareAtPrice =
              typeof compareAtPrice === "number" &&
              compareAtPrice > variant.priceInCents;

            return (
              <div
                className={`personalized-addons__variant-row${
                  variant.isOutOfStock
                    ? " personalized-addons__variant-row--unavailable"
                    : ""
                }`}
                data-test={`personalized-addon-variant-${variant.variantId}`}
                key={variant.variantId}
              >
                <div className="personalized-addons__variant-copy">
                  <span>{getVariantLabel(variant)}</span>
                  <small>
                    {variant.isOutOfStock
                      ? "Ausverkauft"
                      : (unitPrice ?? "Preis je Einheit")}
                  </small>
                </div>
                <div
                  aria-label={`Menge für ${getVariantLabel(variant)}`}
                  className={`personalized-addons__stepper${
                    quantity > 0 ? " personalized-addons__stepper--active" : ""
                  }`}
                >
                  <button
                    aria-label={`${getVariantLabel(variant)} entfernen`}
                    data-test={`personalized-addon-minus-${variant.variantId}`}
                    disabled={quantity === 0 || variant.isOutOfStock}
                    onClick={() =>
                      onQuantityChange(addon, variant.variantId, quantity - 1)
                    }
                    type="button"
                  >
                    −
                  </button>
                  <span
                    aria-live="polite"
                    data-test={`personalized-addon-quantity-${variant.variantId}`}
                  >
                    {quantity}
                  </span>
                  <button
                    aria-label={`${getVariantLabel(variant)} hinzufügen`}
                    data-test={`personalized-addon-plus-${variant.variantId}`}
                    disabled={variant.isOutOfStock}
                    onClick={() =>
                      onQuantityChange(addon, variant.variantId, quantity + 1)
                    }
                    type="button"
                  >
                    +
                  </button>
                </div>
                <div className="personalized-addons__variant-price">
                  {hasCompareAtPrice ? (
                    <s>{formatMoney(compareAtPrice ?? variant.priceInCents)}</s>
                  ) : null}
                  <strong
                    className={
                      hasCompareAtPrice
                        ? "personalized-addons__sale-price"
                        : undefined
                    }
                  >
                    {formatMoney(variant.priceInCents)}
                  </strong>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <>
          <div className="personalized-addons__divider">
            <div></div>
          </div>
          <div className="personalized-addons__card-action-row">
            <div className="personalized-addons__card-footer-copy">
              <div className="personalized-addons__card-price">
                {isAvailable ? (
                  <>
                    {hasDisplayedDiscount ? (
                      <s>{formatMoney(displayedTotals.compareAtPrice)}</s>
                    ) : null}
                    <strong
                      className={
                        hasDisplayedDiscount
                          ? "personalized-addons__sale-price"
                          : undefined
                      }
                    >
                      {showStartingPrice ? "Ab " : null}
                      {formatMoney(displayedTotals.price)}
                    </strong>
                  </>
                ) : (
                  <span>Nicht verfügbar</span>
                )}
              </div>
              {recommendationLabel ? (
                <p
                  className="personalized-addons__recommendation personalized-addons__recommendation--footer"
                  data-test={`personalized-addon-recommendation-${addon.productId}`}
                >
                  {recommendationLabel} · anpassbar
                </p>
              ) : null}
            </div>
            <button
              className="personalized-addons__secondary-button"
              data-test={`personalized-addon-add-${addon.productId}`}
              disabled={!isAvailable}
              onClick={() => onAdd(addon)}
              type="button"
            >
              {isAvailable ? "Hinzufügen" : "Ausverkauft"}
            </button>
          </div>
        </>
      )}

      {isSelected ? (
        <button
          className="personalized-addons__remove-button"
          data-test={`personalized-addon-remove-${addon.productId}`}
          onClick={() => onRemove(addon)}
          type="button"
        >
          Entfernen
        </button>
      ) : null}
    </article>
  );
}
